// @flow

import { all, put, takeEvery, call, select } from "redux-saga/effects";
import { push } from "connected-react-router";
import type { Action } from "redux-action";
import config from "@config";
import { removeNullAndUndefinedProps } from "@transformers/object";

import http from "@http";
import { route } from "@routes/helper";
import { isFirstCareUser } from "@modules/account/selectors";
import actionCreators from "@modules/service/actionCreator";
import {
  GetAlertsList,
  GetAlert,
  GetAlertSchema,
  AddAlert,
  StartDraftAlert,
  UpdateAlert,
  UpdateRecipientAssignments,
  UpdateAlertAssignments,
  DisableAlert,
  DiscardAlertDraft,
  SubmitAlertForReview,
  CancelAlertReview,
  CancelAlertReviewAndDiscard,
} from "@modules/service/constants/actions";

import { ApprovalStateEnum } from "@modules/service/constants";
import type {
  ApprovableContainer,
  HierarchyAssignment,
  HierarchyAssignmentSummary,
} from "@modules/service/types";
import type { Alert } from "@modules/service/reducer/alerts/types";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import { parseSchema } from "@utils/schema";

type ListResponse<T> = $Exact<{
  content: T,
  paginations: any,
}>;

type ItemResponse<T> = $Exact<T>;

// READ

type AlertListRequest = $Exact<{
  clientId: string,
}>;

export function* onGetAlertsList({
  payload,
}: Action<AlertListRequest>): Generator<
  *,
  void,
  { data: ListResponse<Array<ApprovableContainer<Alert>>> },
> {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      // TODO check for v2
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/alert/configuration`,
      {
        params: {
          limit: 100,
        },
      },
    );

    yield put(actionCreators.GetAlertsListSuccess(response.data.content));
  } catch (e) {
    yield put(actionCreators.GetAlertsListError(e.response));
  }
}

type AlertRequest = AlertListRequest &
  $Exact<{
    alertId: string,
  }>;

export function* onGetAlert({
  payload,
}: Action<AlertRequest>): Generator<*, void, { data: ItemResponse<ApprovableContainer<Alert>> }> {
  try {
    if (!payload || !payload.clientId || !payload.alertId) {
      throw Error("Invalid payload provided");
    }

    const stableResponse = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/alert/configuration/${payload.alertId}`,
    );
    const assignmentsResponse = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/alert/configuration/${payload.alertId}/assignment`,
    );

    yield all([
      put(actionCreators.GetAlertSuccess(stableResponse.data)),
      put(actionCreators.SetAlertAssignments(assignmentsResponse.data)),
    ]);
  } catch (e) {
    yield put(actionCreators.GetAlertError(e.response));
  }
}

// REFRESH

type RefreshRequest = $Exact<{
  clientId: string,
  alertId?: string,
}>;

export function* onRefresh(params: RefreshRequest): Generator<*, void, Object> {
  yield put(actionCreators.GetAlertsList({ clientId: params.clientId }));

  if (params.alertId) {
    yield put(
      actionCreators.GetAlert({
        clientId: params.clientId,
        alertId: params.alertId,
      }),
    );
  }
}

// CREATE

type GetAlertSchemaRequest = $Exact<{
  clientId: string,
}>;

export function* onGetAlertSchema({
  payload,
}: Action<GetAlertSchemaRequest>): Generator<*, void, Object> {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }

    const schema = yield parseSchema("AlertConfigurationRevisionDTO", {
      clientId: payload.clientId,
    });

    yield put(actionCreators.GetAlertSchemaSuccess(schema));
  } catch (e) {
    yield put(actionCreators.GetAlertSchemaError(e.response));
  }
}

export function* onStartDraftAlert({
  payload,
}: Action<AlertRequest>): Generator<*, void, { data: ItemResponse<ApprovableContainer<Alert>> }> {
  try {
    if (!payload || !payload.clientId || !payload.alertId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/alert/configuration/${payload.alertId}`,
    );

    yield all([
      put(actionCreators.SetAlertAssignments([])),
      put(actionCreators.StartDraftAlertSuccess(response.data)),
      put(actionCreators.SetShowEditAlertModal(true)),
    ]);
  } catch (e) {
    yield put(actionCreators.StartDraftAlertError(e.response));
  }
}

type AlertAddRequest = AlertListRequest &
  $Exact<{
    draft: Alert,
    assignments: Array<HierarchyAssignment>,
  }>;

export function* onAddAlert({ payload }: Action<AlertAddRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.draft || !payload.assignments) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.post,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/alert/configuration`,
      payload.draft,
    );

    if (payload.assignments.length) {
      yield all([
        ...payload.assignments.map((a: HierarchyAssignment) => {
          return call(
            http.post,
            // TODO check for v2
            `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/alert/configuration/${response.data.id}/assignment`,
            a,
          );
        }),
      ]);
    }

    yield call(onRefresh, { clientId: payload.clientId });

    yield all([
      put(actionCreators.AddAlertSuccess(response.data)),
      put(actionCreators.SetShowAddAlertModal(false)),
      put(sendSuccessMessage("DRAFT_CREATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.AddAlertError(e.response));
  }
}

// UPDATE

export function* onUpdateRecipientAssignments(draft: Object): Generator<*, void, *> {
  try {
    if (!draft || !draft.alertId || !draft.clientId || !draft.bespokeRecipients) {
      throw Error("Invalid payload provided");
    }
    const { alertId, clientId, bespokeRecipients } = draft;
    let allApiCalls = [];
    bespokeRecipients.map((recipient: Object) => {
      recipient.assignmentRules &&
        recipient.assignmentRules.map((assignmentData: Object) => {
          const url = `${config.endpointBaseUrl}api/v1/service-admin/client/${clientId}/service-config/absence-management/alert/configuration/${alertId}/bespoke-recipient/${recipient.id}/assignment`;

          allApiCalls.push({ url, data: removeNullAndUndefinedProps(assignmentData) });
        });
    });
    const allArr = [
      ...allApiCalls.map(a => {
        return call(http.post, a.url, a.data);
      }),
    ];
    yield all(allArr);

    yield put(actionCreators.UpdateRecipientAssignmentsSuccess());
  } catch (e) {
    yield put(actionCreators.UpdateRecipientAssignmentsError(e.response));
  }
}

type AlertUpdateRequest = AlertRequest &
  $Exact<{
    draft: Alert,
  }>;

export function* onUpdateAlert({ payload }: Action<AlertUpdateRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.alertId || !payload.draft) {
      throw Error("Invalid payload provided");
    }
    const bespokeRecipients = JSON.parse(JSON.stringify(payload.draft.bespokeRecipients)); //Deep clone...

    const draft = {
      ...payload.draft,
      bespokeRecipients: payload.draft.bespokeRecipients.map(b => {
        delete b.assignmentRules;
        return b;
      }),
    };

    const firstCareUser = yield select(isFirstCareUser);

    const response = yield call(
      http.put,
      // TODO check for v2
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/alert/configuration/${payload.alertId}`,
      draft,
    );

    yield all([
      put(sendSuccessMessage("DRAFT_UPDATED")),
      call(onUpdateRecipientAssignments, {
        clientId: payload.clientId,
        alertId: payload.alertId,
        bespokeRecipients,
      }),
      put(actionCreators.UpdateAlertSuccess(response.data)),
      put(actionCreators.SetShowEditAlertModal(false)),
    ]);

    yield call(onRefresh, { clientId: payload.clientId, alertId: payload.alertId });
  } catch (e) {
    yield put(actionCreators.UpdateAlertError(e.response));
  }
}

type AlertUpdateAssignmentsRequest = $Exact<{
  container: ApprovableContainer<Alert>,
  assignments: Array<HierarchyAssignment>,
}>;

export function* onUpdateAlertAssignments({
  payload,
}: Action<AlertUpdateAssignmentsRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.container || !payload.assignments) {
      throw Error("Invalid payload provided");
    }

    const prevAssignmentsResponse = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.container.clientId}/service-config/absence-management/alert/configuration/${payload.container.id}/assignment`,
    );
    if (prevAssignmentsResponse.data && prevAssignmentsResponse.data.length) {
      yield all([
        ...prevAssignmentsResponse.data.map((a: HierarchyAssignmentSummary) => {
          return call(
            http.delete,
            `/api/v1/service-admin/client/${payload.container.clientId}/service-config/absence-management/alert/configuration/${payload.container.id}/assignment/${a.id}`,
          );
        }),
      ]);
    }

    if (payload.assignments.length) {
      yield all([
        ...payload.assignments.map((a: HierarchyAssignment) => {
          return call(
            http.post,
            `/api/v1/service-admin/client/${payload.container.clientId}/service-config/absence-management/alert/configuration/${payload.container.id}/assignment`,
            a,
          );
        }),
      ]);
    }

    yield call(onRefresh, {
      clientId: payload.container.clientId,
      providerId: payload.container.id,
    });

    yield all([
      put(
        actionCreators.GetAlert({
          clientId: payload.container.clientId,
          alertId: payload.container.id,
        }),
      ),
      put(actionCreators.SetShowEditAlertAssignmentsModal(false)),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateAlertAssignmentsError(e.message));
  }
}

// DISABLE

export type AlertDisableRequest = AlertRequest &
  $Exact<{
    approvable: ApprovableContainer<Alert>,
  }>;

export function* onDisable({
  payload,
}: Action<AlertDisableRequest>): Generator<
  *,
  void,
  { data: ItemResponse<ApprovableContainer<Alert>> },
> {
  try {
    if (!payload || !payload.clientId || !payload.alertId || !payload.approvable) {
      throw Error("Invalid payload provided");
    }

    const draft =
      payload.approvable.approvalState === ApprovalStateEnum.ACTIVE_ONLY
        ? payload.approvable.active
        : payload.approvable.draft;

    if (!draft) {
      throw Error("Invalid draft");
    }

    const response = yield call(
      http.put,
      // TODO check for v2
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/alert/configuration/${payload.alertId}`,
      {
        ...draft,
        details: {
          ...draft.details,
          activeFrom: draft.details.activeTo ? new Date().toISOString() : draft.details.activeFrom,
          activeTo: draft.details.activeTo ? null : new Date().toISOString(),
        },
      },
    );

    yield call(onRefresh, { clientId: payload.clientId, alertId: payload.alertId });

    yield all([
      put(actionCreators.DisableAlertSuccess(response.data)),
      put(actionCreators.SetShowDisableAlertModal(false)),
      put(push(route("ADMINISTRATION_SERVICE_ALERTS", { clientId: payload.clientId }))),
      put(sendSuccessMessage("DRAFT_UPDATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.DisableAlertError(e.response));
  }
}

// STATE TRANSITIONS

export type AlertTransitionRequest = $Exact<{
  clientId: string,
  alertId: string,
}>;

export function* onDiscardDraft({
  payload,
}: Action<AlertTransitionRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.alertId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.delete,
      // TODO check for v2
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/alert/configuration/${payload.alertId}`,
    );

    yield call(onRefresh, { clientId: payload.clientId });

    yield all([
      put(actionCreators.DiscardAlertDraftSuccess()),
      put(actionCreators.SetShowDiscardAlertModal(false)),
      put(push(route("ADMINISTRATION_SERVICE_ALERTS", { clientId: payload.clientId }))),
    ]);
  } catch (e) {
    yield put(actionCreators.DiscardAlertDraftError(e.response));
  }
}

export type AlertSubmitForReviewRequest = $Exact<{
  ...AlertTransitionRequest,
  notes: string,
}>;

export function* onSubmitForReview({
  payload,
}: Action<AlertSubmitForReviewRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.alertId || !payload.notes) {
      throw Error("Invalid payload provided");
    }

    yield call(onRefresh, { clientId: payload.clientId, alertId: payload.alertId });

    yield all([
      put(actionCreators.SubmitAlertForReviewSuccess()),
      put(actionCreators.SetShowSubmitAlertModal(false)),
      put(push(route("ADMINISTRATION_SERVICE_ALERTS", { clientId: payload.clientId }))),
    ]);
  } catch (e) {
    yield put(actionCreators.SubmitAlertForReviewError(e.response));
  }
}

export function* onCancelReview({
  payload,
}: Action<AlertTransitionRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.alertId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.post,
      // TODO check for v2
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/alert/configuration/${payload.alertId}/cancel-review`,
    );

    yield call(onRefresh, { clientId: payload.clientId, alertId: payload.alertId });

    yield all([
      put(actionCreators.CancelAlertReviewSuccess()),
      put(actionCreators.SetShowCancelAlertModal(false)),
      put(push(route("ADMINISTRATION_SERVICE_ALERTS", { clientId: payload.clientId }))),
    ]);
  } catch (e) {
    yield put(actionCreators.CancelAlertReviewError(e.response));
  }
}

export function* onCancelReviewAndDiscard({
  payload,
}: Action<AlertTransitionRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.alertId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.post,
      // TODO check v2
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/alert/configuration/${payload.alertId}/cancel-review`,
    );
    yield call(
      http.delete,
      // TODO check v2
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/alert/configuration/${payload.alertId}/draft`,
    );

    yield call(onRefresh, { clientId: payload.clientId, alertId: payload.alertId });

    yield all([
      put(actionCreators.CancelAlertReviewSuccess()),
      put(actionCreators.SetShowCancelDiscardAlertModal(false)),
      put(push(route("ADMINISTRATION_SERVICE_ALERTS", { clientId: payload.clientId }))),
    ]);
  } catch (e) {
    yield put(actionCreators.CancelAlertReviewError(e.response));
  }
}

// ROOT

export function* AlertsSagas(): Generator<*, void, void> {
  yield all([
    takeEvery(GetAlertsList, onGetAlertsList),
    takeEvery(GetAlert, onGetAlert),
    takeEvery(GetAlertSchema, onGetAlertSchema),
    takeEvery(AddAlert, onAddAlert),
    takeEvery(StartDraftAlert, onStartDraftAlert),
    takeEvery(UpdateAlert, onUpdateAlert),
    takeEvery(UpdateRecipientAssignments, onUpdateRecipientAssignments),
    takeEvery(UpdateAlertAssignments, onUpdateAlertAssignments),
    takeEvery(DisableAlert, onDisable),
    takeEvery(DiscardAlertDraft, onDiscardDraft),
    takeEvery(SubmitAlertForReview, onSubmitForReview),
    takeEvery(CancelAlertReview, onCancelReview),
    takeEvery(CancelAlertReviewAndDiscard, onCancelReviewAndDiscard),
  ]);
}

export default AlertsSagas;
